import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import ContentLoader, { Rect } from 'react-content-loader/native';
import { useTheme } from '@/context/ThemeContext';

/**
 * Suspense fallback for the lazily-loaded artifact editor panels (document,
 * spreadsheet, presentation, PDF preview) in the contextual sidebar.
 *
 * Shaped like a toolbar over a document body so the panel does not jump when the
 * real editor resolves.
 *
 * Text-free, though no longer for the reason it started out as: the prerender
 * teardown used to key off how much text #root held, which a skeleton could
 * satisfy with nothing on screen. That is now gated on an explicit
 * `data-app-ready` signal instead (see lib/appReady.js), so skeleton text is
 * safe. Grey blocks are simply the right call for an editor placeholder — there
 * is no honest copy to show for a document that hasn't loaded.
 */
const createStyles = (colors) => StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
    paddingHorizontal: 14,
    paddingTop: 12,
    gap: 14,
  },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderColor: colors.border_color_secondary,
  },
  body: {
    gap: 10,
    paddingTop: 4,
  },
});

export default function ContextualPanelSkeleton() {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const loaderProps = {
    speed: 1.4,
    backgroundColor: colors.solid_color_tertiary,
    foregroundColor: colors.border_color_secondary,
  };

  return (
    <View style={styles.container}>
      <View style={styles.toolbar}>
        <ContentLoader {...loaderProps} width="100%" height={28}>
          <Rect x="0" y="4" rx="6" ry="6" width="28" height="20" />
          <Rect x="34" y="4" rx="6" ry="6" width="28" height="20" />
          <Rect x="68" y="4" rx="6" ry="6" width="28" height="20" />
          <Rect x="112" y="4" rx="6" ry="6" width="70" height="20" />
        </ContentLoader>
      </View>

      <View style={styles.body}>
        <ContentLoader {...loaderProps} width="100%" height={148}>
          <Rect x="0" y="0" rx="4" ry="4" width="46%" height="18" />
          <Rect x="0" y="34" rx="4" ry="4" width="96%" height="12" />
          <Rect x="0" y="54" rx="4" ry="4" width="90%" height="12" />
          <Rect x="0" y="74" rx="4" ry="4" width="93%" height="12" />
          <Rect x="0" y="94" rx="4" ry="4" width="62%" height="12" />
          <Rect x="0" y="124" rx="4" ry="4" width="88%" height="12" />
        </ContentLoader>
      </View>
    </View>
  );
}
